import { graphql } from '@api/generated';
import { getClient } from '@api/client';
import { CombinedGraphQLErrors } from '@apollo/client';
import { SearchParams as CustomerSearchParams } from '../components/CustomerSearchBox/CustomerSearchBox';
import { CustomersBySearchResponse } from '@components/CustomerSearch/data/types';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';

export const getCustomerBySearchQuery = graphql(`
  query getCustomerBySearch($tenantId: ID, $where: CustomerFilterInput) {
    customers(tenantId: $tenantId, where: $where) {
      items {
        id
        name
        contacts {
          id
          type
          value
          memo
          jobRemindersEnabled
          marketingUpdatesEnabled
          invoiceStatementNotification
        }
        type
        locations {
          id
          active
          name
          address {
            street
            unit
            city
            state
            zip
            country
          }
        }
        tenant {
          id
          name
        }
        doNotService
      }
    }
  }
`);

/**
 * Same as {@link getCustomerBySearchQuery}, but fetches only a 3-location preview plus the total
 * `locationCount` instead of every location. Used when
 * {@link FeatureFlagKeys.featureCustomerLocationsPreview} is on.
 */
export const getCustomerBySearchWithLocationPreviewQuery = graphql(`
  query getCustomerBySearchWithLocationPreview(
    $tenantId: ID
    $where: CustomerFilterInput
  ) {
    customers(tenantId: $tenantId, where: $where) {
      items {
        id
        name
        contacts {
          id
          type
          value
          memo
          jobRemindersEnabled
          marketingUpdatesEnabled
          invoiceStatementNotification
        }
        type
        locationCount
        locations(first: 3) {
          id
          active
          name
          address {
            street
            unit
            city
            state
            zip
            country
          }
        }
        tenant {
          id
          name
        }
        doNotService
      }
    }
  }
`);

async function searchCustomers(
  searchParams: CustomerSearchParams,
  tenantId?: string,
): Promise<CustomersBySearchResponse> {
  const locationPreviewEnabled = FeatureFlagModule.isEnabled(
    FeatureFlagKeys.featureCustomerLocationsPreview,
  );
  const { data, error } = await getClient().query({
    query: locationPreviewEnabled
      ? getCustomerBySearchWithLocationPreviewQuery
      : getCustomerBySearchQuery,
    variables: { tenantId, where: searchParams },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  let failedTenants: string[] = [];

  if (error) {
    if (!CombinedGraphQLErrors.is(error)) {
      throw new Error(error.message);
    }

    const { errors } = error;

    const failedTenantsInfo = errors.find(
      (error) => error.extensions?.failedTenants,
    );

    if (failedTenantsInfo?.extensions?.failedTenants) {
      failedTenants = failedTenantsInfo.extensions.failedTenants as string[];
    }

    // Throw actual GraphQL errors (not failedTenants warnings).
    const otherErrors = errors.filter(
      (error) => !error.extensions?.failedTenants,
    );
    if (otherErrors.length > 0) {
      throw new Error(otherErrors[0].message);
    }
  }

  return {
    customers: data?.customers?.items ?? [],
    failedTenants: failedTenants,
  };
}

export default searchCustomers;
